import axios from "core/axios";
import moment from "moment";

import { phoneFormatter } from "utils/helpers";
import { IsignUpResponse } from "redux/ducks/auth/authTypes";

type signInProps = {
  phone: string;
  countryCode: string;
  oneTimePassword: string;
  pinCode?: string;
};

type signUpProps = {
  phone: string;
  countryCode: string;
  oneTimePassword: string;
  name: string;
  sex: string;
  interestedGender: string;
  birthday: string;
  cityId: number;
};

const apiKey = process.env.REACT_APP_API_KEY;
const msgBaseUrl = process.env.REACT_APP_MSG_URL

export default {
  refreshToken: (from?:string) => {
    return axios.get(
      `/auth/refreshToken?refreshToken=${window.localStorage.refreshToken}&idProfile=${window.localStorage.idProfile}&fingerprint=${window.localStorage.fingerPrint}&from=${from}`,
      {
        headers: {
          "X-API-KEY": apiKey,
        },
      }
    );
  },
  sendCode: (phone: string, countryCode: string) => {
    const formattedPhone = encodeURI(countryCode) + phoneFormatter(phone);
    return axios.get(
      `/auth/sendCode?phoneNumber=${encodeURIComponent(formattedPhone)}`,
      {
        headers: {
          "X-API-KEY": apiKey,
        },
      }
    );
  },
  signIn: ({ phone, countryCode, oneTimePassword, pinCode }: signInProps) => {
    const formattedPhone = encodeURI(countryCode) + phoneFormatter(phone);
    return axios.get<IsignUpResponse>(
      `/auth/signIn?phoneNumber=${encodeURIComponent(
        formattedPhone
      )}&oneTimePassword=${oneTimePassword}${
        pinCode ? `&pinCode=${pinCode}` : ""
      }&fingerprint=${window.localStorage.fingerPrint}`,
      {
        headers: {
          "X-API-KEY": apiKey,
        },
      }
    );
  },
  signUp: ({
    phone,
    countryCode,
    oneTimePassword,
    name,
    sex,
    interestedGender,
    birthday,
    cityId,
  }: signUpProps) => {
    const formattedPhone = countryCode + phoneFormatter(phone);

    const data = {
      name,
      sex,
      sexPreference: interestedGender,
      birthday: moment(birthday).format("YYYY-MM-DD").toString(),
      idCity: cityId,
    };

    return axios.post<IsignUpResponse>(
      `/auth/signUp?phoneNumber=${encodeURIComponent(
        formattedPhone
      )}&oneTimePassword=${oneTimePassword}&fingerprint=${window.localStorage.fingerPrint}`,
      data,
      {
        headers: {
          "X-API-KEY": apiKey,
        },
      }
    );
  },
  setPincode: (pincode: string) => {
    return axios.post(`/auth/createPinCode?pinCode=${pincode}`, {}, {
      headers: {
        'Authorization': `Bearer ${window.localStorage.accessToken}`
      }
    })
  },
  signOut: (deviceId?: number) => {
    const url = `/auth/signOut${deviceId?`?deviceId=${deviceId}`:""}`;
    console.log('authApi.signOut called, URL:', url);
    console.log('Access token exists:', !!window.localStorage.accessToken);
    return axios.delete(url, {
      headers: {
        'Authorization': `Bearer ${window.localStorage.accessToken}`
      }
    })
  },
  getCountries: () => {
    return axios.get(`/entities/getCountries`, {
      headers: {
        "X-API-KEY": apiKey,
      },
    })
  },
  getCities: (countryId: number) => {
    return axios.get(`/entities/getCities?idCountry=${countryId}`, {
      headers: {
        "X-API-KEY": apiKey,
      },
    })
  },
  // Локализованная гео-база (страны+города+флаги) как в приложении.
  // Путь БЕЗ /api: baseURL (REACT_APP_BACKEND_URL) уже оканчивается на /api,
  // иначе получался двойной /api/api/v1/geo → 404 → фоллбэк без флагов.
  getGeo: (lang: string) => {
    return axios.get(`/v1/geo?lang=${encodeURIComponent(lang)}`, {
      headers: {
        "X-API-KEY": apiKey,
      },
    })
  },
  getSocket: () => {
    return axios.put(`/messages/registerWebsocket`, {})
  },
  fbSignIn: (token: string) => {
    return axios.post(
      `/auth/facebookSignIn?facebookToken=${token}&fingerprint=${window.localStorage.fingerPrint}`, {},
      {
        headers: {
          "X-API-KEY": apiKey,
        },
      }
    );
  },
  fbSignUp: (token: string, {
    name,
    sex,
    interestedGender,
    birthday,
    cityId,
  }: signUpProps) => {
    const data = {
      name,
      sex,
      sexPreference: interestedGender,
      birthday: moment(birthday).format("YYYY-MM-DD").toString(),
      idCity: cityId,
    };

    return axios.post(
      `/auth/confirmationProfile?confirmationToken=${token}&fingerprint=${window.localStorage.fingerPrint}`, 
      data,
      {
        headers: {
          "X-API-KEY": apiKey,
        },
      }
    );
  },
  googleSignIn: (code: string) => {
    // Проверяем, что токен не пустой и выглядит как JWT
    if (!code || code.trim().length === 0) {
        console.error('googleSignIn: Empty token provided');
        return Promise.reject(new Error('Google token is empty'));
      }
      
      // JWT токен должен содержать 3 части, разделенные точками
      const parts = code.split('.');
      if (parts.length !== 3) {
        console.error('googleSignIn: Invalid JWT token format');
        return Promise.reject(new Error('Invalid Google token format'));
      }
      
    const fingerprint = window.localStorage.fingerPrint || "";
    const url = `/auth/googleSignIn?googleToken=${encodeURIComponent(code)}&fingerprint=${encodeURIComponent(fingerprint)}&_=${Date.now()}`;
    console.log('authApi.googleSignIn called');
    console.log('Base URL:', axios.defaults.baseURL);
    console.log('Full URL:', axios.defaults.baseURL + url);
    console.log('Fingerprint:', window.localStorage.fingerPrint);
    console.log('Token length:', code.length);
    console.log('Token preview (first 50 chars):', code.substring(0, 50));
    
    return axios.post(
      url,
       {},
      {
        headers: {
          "X-API-KEY": apiKey,
          "Cache-Control": "no-cache, no-store, must-revalidate",
          "Pragma": "no-cache",
        },
      }
    );
  },
  appleSignIn: (code: string, identityToken?: string, redirectUri?: string) => {
    if (!code || code.trim().length === 0) {
      return Promise.reject(new Error('Apple authorization code is empty'));
    }

    return axios.post(
      `/auth/appleSignIn?authorizationCode=${encodeURIComponent(code)}${identityToken ? `&identityToken=${encodeURIComponent(identityToken)}` : ""}${redirectUri ? `&redirectUri=${encodeURIComponent(redirectUri)}` : ""}&fingerprint=${window.localStorage.fingerPrint}&_=${Date.now()}`,
      {},
      {
        headers: {
          "X-API-KEY": apiKey,
          "Cache-Control": "no-cache, no-store, must-revalidate",
          "Pragma": "no-cache",
        },
      }
    );
  },
  googleSignUp: (code: string, {
    name,
    sex,
    interestedGender,
    birthday,
    cityId,
  }: signUpProps) => {
    const data = {
      name,
      sex,
      sexPreference: interestedGender,
      birthday: moment(birthday).format("YYYY-MM-DD").toString(),
      idCity: cityId,
    };

    return axios.post(
      `/auth/confirmationProfile?confirmationToken=${code}&fingerprint=${window.localStorage.fingerPrint}`, 
      data,
      {
        headers: {
          "X-API-KEY": apiKey,
        },
      }
    );
  },
};
